import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { LandingPage } from './components/LandingPage';
import { FoundingFundersPopup } from './components/FoundingFundersPopup';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
const NationalNetworkPage = lazy(() => import('./pages/NationalNetworkPage').then(m => ({
  default: m.NationalNetworkPage
})));
const EncuentroCatrachoMiamiPage = lazy(() => import('./pages/EncuentroCatrachoMiamiPage').then(m => ({
  default: m.EncuentroCatrachoMiamiPage
})));
const VoicesOfImpactNYPage = lazy(() => import('./pages/VoicesOfImpactNYPage').then(m => ({
  default: m.VoicesOfImpactNYPage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="m5ni3i3y71t7">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="h6sve52v0t3t">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="h7lzszecj6is" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="c9kc215z6j0z" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="14wewwz6pfkez" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="2thylwyad2ca8" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="33go5cvhwo4sl">
      <Navbar data-id="element-7" data-mp="33el7jpaih7db" />
      <FoundingFundersPopup data-id="element-8" data-mp="2yi36p0iaxt69" />
      <main data-id="element-9" data-mp="2yiw59e73xcdb">
        <Hero data-id="element-10" data-mp="2yj5u2ygf1dhf" />
        <ConsularBanner data-id="element-11" data-mp="2yjo20scfxg56" />
        <Mission data-id="element-12" data-mp="3i3mx6rkod17u" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-14" data-mp="3n39at24f0jh6" />} data-id="element-13" data-mp="3n2pq5dndrgzg">
          <OurPeopleStories data-id="element-15" data-mp="3n1ep3llbknc1" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-17" data-mp="385yoexq8ht6o" />} data-id="element-16" data-mp="385gx8fifi55m">
          <MakeDifferenceCTA data-id="element-18" data-mp="384nl0z0pang3" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-19" data-mp="3d4jrd4wveqyt">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-20" data-mp="3d38mt2vlpf6n" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-22" data-mp="2zb27tk46pe8s" />} data-id="element-21" data-mp="2zbkelaoh3auy">
          <NewsBlog data-id="element-23" data-mp="343lmkkelyff6" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-25" data-mp="3456gc7t3lubz" />} data-id="element-24" data-mp="344n85it92kb5">
          <MediaScroller data-id="element-26" data-mp="2pez96o8jjxql" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-28" data-mp="2pgsfg2f8sfy2" />} data-id="element-27" data-mp="2pga8obuyejbw">
          <CommunityStrength data-id="element-29" data-mp="2ue20z6zig7gm" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-31" data-mp="2uebqktgn8cls" />} data-id="element-30" data-mp="2uetywogkyha2">
          <MissionPanels data-id="element-32" data-mp="2ge9c7h7jo1ba" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-34" data-mp="2gf2oexp9vj0t" />} data-id="element-33" data-mp="2gfkh5kcq7f3z">
          <Leadership data-id="element-35" data-mp="2kfuyft5yxry5" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-37" data-mp="2kfctm858q5em" />} data-id="element-36" data-mp="2ket8yjo7h2ww">
          <AdvisoryCouncil data-id="element-38" data-mp="2khp9mfrfu5p1" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-40" data-mp="30uvegmavv8k0" />} data-id="element-39" data-mp="30uc7u1qoo6la">
          <MakeDifferenceCTA data-id="element-41" data-mp="30t15858z54vr" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-43" data-mp="2vwtxc6x0pjb6" />} data-id="element-42" data-mp="2vxc0zom0ezt4">
          <BusinessInvestments data-id="element-44" data-mp="2vug483lweh69" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-46" data-mp="2qvmpy8ysem51" />} data-id="element-45" data-mp="2qw691t06bgkn">
          <ConsularAffairs data-id="element-47" data-mp="2qz0thkj6t1e2" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-49" data-mp="2lxl5bjlpqerg" />} data-id="element-48" data-mp="2lx31o1wq0y9i">
          <SocialMediaVoices data-id="element-50" data-mp="2m0h3n91h35gf" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-52" data-mp="2hy2lys9jibqq" />} data-id="element-51" data-mp="2hxkro16fu7lg">
          <NonprofitDirectory data-id="element-53" data-mp="2hyceami24uzl" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-55" data-mp="2d486k10ywixg" />} data-id="element-54" data-mp="2d4rmjc724x8u">
          <HondurasQuiz data-id="element-56" data-mp="2d3ybw04z9nlf" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-58" data-mp="287hnd4tv977t" />} data-id="element-57" data-mp="285wsfe3n3m9f">
          <ExploreHonduras data-id="element-59" data-mp="284lo9d6a8chs" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-61" data-mp="238xzuzzmjndc" />} data-id="element-60" data-mp="238elft96nh42">
          <FAQ data-id="element-62" data-mp="235inw618yug5" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-63" data-mp="1y8s87jtcu0v9">
        <Footer data-id="element-64" data-mp="1y7quf7kyf09u" />
      </Suspense>
      <Suspense fallback={null} data-id="element-65" data-mp="1u6x5cavrzffd">
        <ChatbaseWidget data-id="element-66" data-mp="1u7zw8it3lhva" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  const [showLanding, setShowLanding] = useState(!isSectionAnchor(initialHash));
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showVoicesNY, setShowVoicesNY] = useState(() => window.location.hash === '#/voices-of-impact-ny');
  const [showEncuentroMiami, setShowEncuentroMiami] = useState(() => window.location.hash === '#/encuentro-catracho-miami');
  const [showNationalNetwork, setShowNationalNetwork] = useState(() => window.location.hash === '#/national-network');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      setShowVoicesNY(hash === '#/voices-of-impact-ny');
      setShowEncuentroMiami(hash === '#/encuentro-catracho-miami');
      setShowNationalNetwork(hash === '#/national-network');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    setShowLanding(true);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-68" data-mp="mmxuv2n1bx1c" />} data-id="element-67" data-mp="mlegxioupy2q">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-69" data-mp="mm6dowu6q1ul" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-70" data-mp="vnw01o97mali">
        <Suspense fallback={<SectionLoader data-id="element-72" data-mp="10l5ytuihnym0" />} data-id="element-71" data-mp="10kmkens1rscq">
          <ConsularPage data-id="element-73" data-mp="10juvfvsygsvf" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-74" data-mp="1abqx1gu9314e">
        <Suspense fallback={<SectionLoader data-id="element-76" data-mp="1ebsqx3tquq94" />} data-id="element-75" data-mp="1edc7ywn855by">
          <BusinessMapPage data-id="element-77" data-mp="1ef6ugfe595i5" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-78" data-mp="1ob9thcdp8o0v">
        <Suspense fallback={<SectionLoader data-id="element-80" data-mp="1o8xbx0busfoc" />} data-id="element-79" data-mp="1objk926kiz7m">
          <MembersDirectoryPage data-id="element-81" data-mp="1yfq7e07mbv21" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNY) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-82" data-mp="1tf2fzdfh9c7a">
        <Suspense fallback={<SectionLoader data-id="element-84" data-mp="1og939n80lp86" />} data-id="element-83" data-mp="1ogr6x4x0b5q4">
          <VoicesOfImpactNYPage data-id="element-85" data-mp="1ohj3oj2cb9hz" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showEncuentroMiami) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-86" data-mp="1khs69wib5v4h">
        <Suspense fallback={<SectionLoader data-id="element-88" data-mp="2h4wwc4z09iwa" />} data-id="element-87" data-mp="2h4dd8kxmcogo">
          <EncuentroCatrachoMiamiPage data-id="element-89" data-mp="2c6lmhu8pn8e2" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNationalNetwork) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-90" data-mp="27etqv5ap6ch2">
        <Suspense fallback={<SectionLoader data-id="element-92" data-mp="23f34dds7eiic" />} data-id="element-91" data-mp="23ejl9tqtho2q">
          <NationalNetworkPage data-id="element-93" data-mp="23ds1rhahvgst" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-94" data-mp="2zyaiplonkh0a">
        <Suspense fallback={<SectionLoader data-id="element-96" data-mp="2v4688sg6hqug" />} data-id="element-95" data-mp="2v4o2jjja5uzq">
          <SaveTheDatePage data-id="element-97" data-mp="t7xdepgm23q7" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-98" data-mp="y6qpqek5voos">
        <Suspense fallback={<SectionLoader data-id="element-100" data-mp="ji41toggxoly" />} data-id="element-99" data-mp="y935qm6czoz7">
          <MemberProfilePage slug={memberSlug} data-id="element-101" data-mp="jivyl2lsxsdt" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-102" data-mp="ofmareuhk3ty">
        <Suspense fallback={<SectionLoader data-id="element-104" data-mp="agebklybovpr" />} data-id="element-103" data-mp="agwiccim2sbx">
          <NewsStoryPage postId={newsPostId} data-id="element-105" data-mp="ag4j8rpt2cgw" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-106" data-mp="fe633ev22ftf" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-107" data-mp="ji14aov5zik">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-108" data-mp="lcp5xvfm7qv" />
      </LanguageProvider>
    </>;
}